/**
 * The Weave — Member session (?wm= token verification).
 *
 * Friendly-fence pattern. Reads a URL-borne base64 claims object once on page load,
 * validates plan + expiry, and persists in sessionStorage so the user stays in
 * member-mode for the tab session even after the URL is stripped.
 *
 * This will be retired the moment Memberstack's cross-subdomain cookie SSO ships.
 */

export const QUALIFYING_PLANS = [
    "prc_1-club-monthly-txnv03iu",
    "prc_1-annual-n2170ed3",
    "pln_weave-team-np3a09sc", // Weave Team free plan — cofounders + comp access
];

const STORAGE_KEY = "weave_member_session";
const AWAKE_DASHBOARD_URL = "https://awake.wearetheweave.co.uk/dashboard";

/**
 * Read `?wm=` token from the current URL. Returns the validated claims or null.
 * Provided as-is from the awake spec.
 */
export function readWmToken() {
    try {
        const raw = new URLSearchParams(window.location.search).get("wm");
        if (!raw) return null;
        const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(escape(atob(b64)));
        const claims = JSON.parse(json);

        if (claims.v !== 1) return null;
        const now = Math.floor(Date.now() / 1000);
        if (typeof claims.exp !== "number" || claims.exp < now) return null;
        if (!QUALIFYING_PLANS.includes(claims.p)) return null;

        return claims;
    } catch {
        return null;
    }
}

/**
 * Run once at app boot: read ?wm= from URL, persist to sessionStorage, strip URL.
 * Safe to call multiple times — idempotent.
 */
export function initMemberSessionFromUrl() {
    const claims = readWmToken();
    if (claims) {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(claims));
        } catch {}
        // Cosmetic: strip ?wm= from the URL bar
        try {
            const u = new URL(window.location.href);
            u.searchParams.delete("wm");
            window.history.replaceState({}, "", u.toString());
        } catch {}
    }
}

/**
 * Return current member claims, or null if no session / expired.
 * Self-heals: clears storage if expired.
 */
export function getMemberSession() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const claims = JSON.parse(raw);
        const now = Math.floor(Date.now() / 1000);
        if (!claims.exp || claims.exp < now) {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return claims;
    } catch {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {}
        return null;
    }
}

export function isPayingMember() {
    return getMemberSession() !== null;
}

/**
 * Log out: clear local session and redirect to awake's dashboard (single source
 * of truth for real logout).
 */
export function memberLogout() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    window.location.assign(AWAKE_DASHBOARD_URL);
}

export const AWAKE_URLS = {
    dashboard: AWAKE_DASHBOARD_URL,
    login: "https://awake.wearetheweave.co.uk/login",
    join: "https://awake.wearetheweave.co.uk/join",
};
